import { Component, DestroyRef, OnInit, inject, signal } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Notice } from '@app/_models/notice.model';
import { NoticeService } from '@app/_services/notice.service';
import { AuthenticationService, UserService } from '@app/_services';
import { AccessManagementService } from '@app/_services/access-management.service';

@Component({
    selector: 'app-notices',
    templateUrl: './notices.component.html',
    styleUrls: ['./notices.component.css'],
    standalone: false
})
export class NoticesComponent implements OnInit {

  constructor(private route: ActivatedRoute,
    private noticeService: NoticeService,
    private userService: UserService,
    private accessManagementService: AccessManagementService,
    private authenticationService: AuthenticationService) { }

  propertyId = signal<number | null>(null);
  unitNumber = signal<number | null>(null);

  propertyNotices = signal<Notice[]>([]);
  unitNotices = signal<Notice[]>([]);

  loadingProperty = signal(false);
  loadingUnit = signal(false);

  private destroyRef = inject(DestroyRef);

  ngOnInit(): void {
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(params => {
        const propertyId = Number(params.get('propertyid'));
        if (!params.get('propertyid') || Number.isNaN(propertyId)) {
          this.propertyNotices.set([]);
          this.unitNotices.set([]);
          return;
        }

        this.propertyId.set(propertyId);
        this.loadPropertyNotices(propertyId);
        this.resolveUnit(propertyId);
      });
  }

  private resolveUnit(propertyId: number): void {
    const username = this.authenticationService.currentUserValue?.username;
    if (!username) {
      return;
    }

    this.accessManagementService.getAllAccess(username)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: access => {
          const entry = (access ?? []).find(a => Number(a.property_id) === propertyId);
          const unitNumber = this.toUnitNumber(entry?.unit_number?.toString());
          if (unitNumber !== null) {
            this.unitNumber.set(unitNumber);
            this.loadUnitNotices(propertyId, unitNumber);
          } else {
            this.resolveUnitFromProfile(propertyId, username);
          }
        },
        error: () => this.resolveUnitFromProfile(propertyId, username)
      });
  }

  private resolveUnitFromProfile(propertyId: number, username: string): void {
    this.userService.getUserProfile(username)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: profile => {
          const unitNumber = this.toUnitNumber(profile?.apartmentnumber);
          if (unitNumber !== null) {
            this.unitNumber.set(unitNumber);
            this.loadUnitNotices(propertyId, unitNumber);
          }
        }
      });
  }

  // Backend unit route only accepts integer unit numbers; 0 denotes property-wide.
  private toUnitNumber(value: string | null | undefined): number | null {
    const unit = Number(String(value ?? '').trim());
    return Number.isInteger(unit) && unit > 0 ? unit : null;
  }

  private loadPropertyNotices(propertyId: number): void {
    this.loadingProperty.set(true);
    this.noticeService.getPropertyNotices(propertyId)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.propertyNotices.set((data ?? []).filter(n => !n.unit_number));
          this.loadingProperty.set(false);
        },
        error: () => {
          this.propertyNotices.set([]);
          this.loadingProperty.set(false);
        }
      });
  }

  private loadUnitNotices(propertyId: number, unitNumber: number): void {
    this.loadingUnit.set(true);
    this.noticeService.getUnitNotices(propertyId, unitNumber)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe({
        next: data => {
          this.unitNotices.set(data ?? []);
          this.loadingUnit.set(false);
        },
        error: () => {
          this.unitNotices.set([]);
          this.loadingUnit.set(false);
        }
      });
  }
}
